
import { useNavigate } from "react-router-dom";
import { BlogPost } from "@/types/blogTypes";
import { saveBlogPost, deleteBlogPost } from "@/services/blogPostService";
import { uploadImage } from "@/utils/imageUpload";
import { PostEditorState } from "@/types/postEditorTypes";

export const usePostEditorActions = (
  postId: string | undefined,
  state: PostEditorState,
  setIsPending: (isPending: boolean) => void,
  setShowDeleteDialog: (show: boolean) => void,
  setIsDraft: (isDraft: boolean) => void,
  toast: any
) => {
  const navigate = useNavigate();

  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      return await uploadImage(file, "blog/featured");
    } catch (error) {
      toast({
        title: "Image upload failed",
        description: "There was a problem uploading your image. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDeletePost = async () => {
    if (!postId) return;
    
    setIsPending(true);
    
    try {
      await deleteBlogPost(postId);
      
      toast({
        title: "Post deleted",
        description: "The blog post has been deleted successfully.",
      });
      
      setShowDeleteDialog(false);
      navigate("/admin/blog");
    } catch (error: any) {
      toast({
        title: "Error deleting post",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
      setIsPending(false);
    }
  };

  const handleSaveAsDraft = async () => {
    const { title, slug, excerpt, content, featuredImageAlt, featuredImageTitle } = state;
    
    if (!title || !slug || !excerpt || !content) {
      toast({
        title: "Missing fields",
        description: "Please fill out all required fields to save as draft",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsPending(true);
      
      // Prepare post data
      const postData: Partial<BlogPost> = {
        id: postId,
        title,
        slug,
        excerpt,
        content,
        featured_image: state.featuredImage,
        published: false,
        // Store additional metadata
        metadata: {
          author: state.author,
          seo_description: state.seoDescription,
          theme: state.theme,
          categories: state.categories,
          featured_image_alt: state.featuredImageAlt,
          featured_image_title: state.featuredImageTitle
        }
      };
      
      // Save blog post
      await saveBlogPost(postData);
      
      toast({
        title: "Draft saved!",
        description: "Your blog post draft has been updated successfully.",
      });
      
      setIsDraft(true);
      setIsPending(false);
    } catch (error: any) {
      toast({
        title: "Error saving draft",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
      setIsPending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { 
      title, 
      slug, 
      excerpt, 
      content, 
      isDraft, 
      featuredImage, 
      featuredImageAlt,
      featuredImageTitle,
      author, 
      seoDescription, 
      theme, 
      categories 
    } = state;
    
    // Validation
    if (!title || !slug || !excerpt || !content) {
      toast({
        title: "Missing fields",
        description: "Please fill out all required fields",
        variant: "destructive",
      });
      return;
    }
    
    setIsPending(true);
    
    try {
      // Prepare post data
      const postData: Partial<BlogPost> = {
        id: postId,
        title,
        slug,
        excerpt,
        content,
        featured_image: featuredImage,
        published: !isDraft,
        // Store additional metadata
        metadata: {
          author,
          seo_description: seoDescription,
          theme,
          categories,
          featured_image_alt: featuredImageAlt,
          featured_image_title: featuredImageTitle
        }
      };
      
      // Save blog post
      await saveBlogPost(postData);
      
      toast({
        title: isDraft ? "Draft updated!" : "Post updated!",
        description: "Your blog post has been updated successfully.",
      });
      
      setTimeout(() => {
        navigate("/admin/blog");
      }, 1500);
    } catch (error: any) {
      toast({
        title: "Error updating post",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    handleImageUpload,
    handleDeletePost,
    handleSaveAsDraft,
    handleSubmit
  };
};
